import React from 'react';
import { CommandBar, DefaultButton, Icon, ICommandBarItemProps, Panel, PanelType, TextField } from '@fluentui/react';
import { IGlobalStorefrontSettings, IStorefrontSettings, IStorefrontTheme } from '../../../../models/Storefront';
import '../StorefrontEditor.css';
import { EditPencil, ElementPropertyRows, elementStyleFrom, StyleOverridesGroup, StylePartAccordions } from '../styleControls';
import { STYLE_PART_ICONS, styleTargetForKey } from '../stylesConfig';
import { resolvedStyleValue } from '../styleConfig';
import { lintStyleSections } from '../headerFooterConfig';
import RawJsonEditor from '../RawJsonEditor';
import ImageEditor from '../../../Inputs/ImageEditor';
import MarkdownView from 'react-showdown';
import {
    ILoginPageConfig, LOGIN_PAGE_CONTENT_KEYS, LOGIN_PAGE_PART_LABELS, LOGIN_PAGE_PART_TOOLTIPS,
    LOGIN_PAGE_STYLE_KEY_BY_PART, LOGIN_PAGE_STYLE_PARTS, LoginPageStylePart, parseLoginPageConfig,
} from '../loginPageConfig';
import PreviewCanvas from '../PreviewCanvas';

// The Sign In page editor: a live mock of the storefront's auth pages with click-to-edit on every
// styleable element, matching the Cart/Checkout/Product page editors.
//
// It draws NO header or footer, unlike every other page editor here. That is not an omission — the
// runtime's auth pages are a bare full-screen backdrop with a centered card (LoginPageShell), with no
// nav at all, and drawing chrome the shopper will never see would make the preview a lie.
//
// One editor covers four runtime pages (sign-in, sign-up, the emailed-token landing and the poll
// confirm page) because they share one config on purpose: a shopper moves between them inside a
// single flow, and letting them drift is how a branded sign-in hands off to a page that looks like
// somebody else's product. The preview picker switches which one is drawn.

interface IStorefrontLoginPageEditorProps {
    globalSettings: IGlobalStorefrontSettings;
    globalTheme?: IStorefrontTheme;
    storefrontSettings: IStorefrontSettings;
    storefrontTheme?: IStorefrontTheme;
    editMode?: boolean;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
}

// The three topical flyouts the command bar opens. Each owns a group of the config's style parts,
// and the two with content fields attached (the backdrop image on Page, the logo and text on
// Branding) carry those above the accordions — so a merchant never has to leave the editor to set
// the things the preview is showing them.
type PanelKey = 'page' | 'branding' | 'form';

const PARTS_BY_PANEL: Record<PanelKey, LoginPageStylePart[]> = {
    page: ['page', 'box'],
    branding: ['logo', 'heading', 'intro'],
    form: ['input', 'primaryButton', 'secondaryButton', 'message'],
};

const PART_ICONS: Record<LoginPageStylePart, string> = {
    page: STYLE_PART_ICONS.band,
    box: STYLE_PART_ICONS.card,
    logo: STYLE_PART_ICONS.image,
    heading: STYLE_PART_ICONS.heading,
    intro: STYLE_PART_ICONS.body,
    input: STYLE_PART_ICONS.input,
    primaryButton: STYLE_PART_ICONS.base,
    secondaryButton: STYLE_PART_ICONS.base,
    message: 'Message',
};

// Which runtime page the preview draws. The two sign-in entries are the same page at different
// steps of the shared sign-in state machine: `code` is only reachable on a storefront whose login
// method is "code", and it is the step most worth seeing, since it is the one that isn't just an
// email box.
type PreviewMode = 'signin' | 'code' | 'signup';

const PREVIEW_LABELS: Record<PreviewMode, string> = {
    signin: 'Sign in',
    code: 'Sign in — code entry',
    signup: 'Sign up',
};

const StorefrontLoginPageEditor: React.FC<IStorefrontLoginPageEditorProps> = (props) => {
    // Same resolution order the other page editors use: named style → storefront theme → global.
    const backgroundColor = resolvedStyleValue(props.storefrontSettings, 'backgroundColor') || props.storefrontTheme?.backgroundColor || props.globalSettings.backgroundColor || props.globalTheme?.backgroundColor || '#fafafa';
    const textColor = resolvedStyleValue(props.storefrontSettings, 'textColor') || props.storefrontTheme?.textColor || props.globalSettings.textColor || props.globalTheme?.textColor || '#242424';
    const defaultFont = resolvedStyleValue(props.storefrontSettings, 'defaultFont') || props.globalSettings.defaultFont || undefined;

    const config: ILoginPageConfig = React.useMemo(
        () => parseLoginPageConfig(props.storefrontSettings.loginPageConfig),
        [props.storefrontSettings.loginPageConfig]);

    const [openPanel, setOpenPanel] = React.useState<{ key: PanelKey; part?: string } | null>(null);
    const closePanel = () => setOpenPanel(null);
    const openAt = (key: PanelKey, part?: string) => setOpenPanel({ key, part });
    // Editor-only: which of the four runtime pages to draw. Defaults to the sign-in page's first
    // step, which is what most shoppers actually see. Not persisted anywhere.
    const [preview, setPreview] = React.useState<PreviewMode>('signin');

    // Re-read from the live settings rather than the memo above, so quick edits across parts can't
    // clobber one another.
    const write = (patch: Partial<ILoginPageConfig>) => {
        const current = parseLoginPageConfig(props.storefrontSettings.loginPageConfig);
        props.onStorefrontSettingsChange({
            ...props.storefrontSettings,
            loginPageConfig: JSON.stringify({ ...current, ...patch }),
        });
    };
    const updatePart = (part: LoginPageStylePart, patch: Partial<ILoginPageConfig[LoginPageStylePart]>) => {
        const current = parseLoginPageConfig(props.storefrontSettings.loginPageConfig);
        write({ [part]: { ...current[part], ...patch } } as Partial<ILoginPageConfig>);
    };

    // The named-Storefront-Style target for a part. These go through the config's own key table
    // rather than styleBulkApply's config-key lookup: this config names its keys for the ROLE
    // ('box', 'heading'), while the style registry names them for the page ('loginBox',
    // 'loginHeading'), so a direct key lookup would resolve to the wrong element type or to nothing.
    const target = (part: LoginPageStylePart) => styleTargetForKey(LOGIN_PAGE_STYLE_KEY_BY_PART[part]);
    // Resolved inline styles for the preview elements. Passing the part means an element carrying a
    // $styleRef resolves against the right part of the referenced Storefront Style.
    const s = (part: LoginPageStylePart) => elementStyleFrom(config[part], target(part)?.part);

    // Click-to-edit: the element opens its panel with its own accordion expanded, and the hover
    // pencil is the visible affordance for the same action. Every element here sits inside the
    // backdrop (itself editable) and most sit inside the card as well, so the click always has to
    // stop before it reaches an ancestor that would open a different part. EditPencil stops
    // propagation on its own.
    const editable = (panel: PanelKey, part: LoginPageStylePart, what: string): React.HTMLAttributes<HTMLElement> => props.editMode
        ? {
            className: 'sf-pencil-host sf-editable',
            title: `Edit the ${what}`,
            onClick: (e) => { e.stopPropagation(); openAt(panel, part); },
        }
        : { className: 'sf-pencil-host' };
    const pencil = (panel: PanelKey, part: LoginPageStylePart, what: string) =>
        props.editMode ? <EditPencil onClick={() => openAt(panel, part)} title={`Edit ${what}`} /> : null;

    // Every part here maps to a named-style target (see stylesConfig's login* keys), so all of them
    // lead with the style picker and fold their raw rows under "Advanced overrides".
    const partAccordion = (part: LoginPageStylePart) => ({
        key: part,
        label: LOGIN_PAGE_PART_LABELS[part],
        iconName: PART_ICONS[part],
        tooltip: LOGIN_PAGE_PART_TOOLTIPS[part],
        content: (
            <StyleOverridesGroup
                rowLayout
                target={target(part)}
                value={config[part]}
                onChange={(patch) => updatePart(part, patch)}
            >
                <ElementPropertyRows settingKeyPrefix={`storefront.loginPage.${part}`} value={config[part]} onChange={(patch) => updatePart(part, patch)} />
            </StyleOverridesGroup>
        ),
    });
    const accordions = (panel: PanelKey) => PARTS_BY_PANEL[panel].map(partAccordion);

    const styleBarItems: ICommandBarItemProps[] = [
        { key: 'page', text: 'Page', title: 'The full-screen backdrop behind the card, its background image, and the card itself.', iconProps: { iconName: 'GridViewMedium' }, checked: openPanel?.key === 'page', onClick: () => openAt('page') },
        { key: 'branding', text: 'Branding', title: 'The logo, the heading and the line of intro text above the form.', iconProps: { iconName: 'Photo2' }, checked: openPanel?.key === 'branding', onClick: () => openAt('branding') },
        { key: 'form', text: 'Form', title: 'The fields, the buttons and the notice bars inside the card.', iconProps: { iconName: 'PageList' }, checked: openPanel?.key === 'form', onClick: () => openAt('form') },
    ];
    // The preview picker sits on the far side, away from the style buttons: it changes what is
    // DRAWN, not what is saved, and mixing it into the same group reads as another style topic.
    const previewBarItems: ICommandBarItemProps[] = [{
        key: 'preview',
        text: `Preview: ${PREVIEW_LABELS[preview]}`,
        iconProps: { iconName: 'RedEye' },
        title: 'Which of the auth pages to draw. All four share this one configuration.',
        subMenuProps: {
            items: (Object.keys(PREVIEW_LABELS) as PreviewMode[]).map((mode) => ({
                key: mode,
                text: PREVIEW_LABELS[mode],
                canCheck: true,
                checked: preview === mode,
                onClick: () => setPreview(mode),
            })),
        },
    }];

    // ── Preview primitives ───────────────────────────────────────────────────────────────
    // Facsimiles of the Fluent v9 controls the runtime pages use, so the mock reads like the real
    // page without pulling a second Fluent version into the admin bundle.
    const fieldBase: React.CSSProperties = {
        width: '100%', padding: '5px 10px', border: '1px solid #d1d1d1', borderBottomColor: '#616161',
        borderRadius: 4, boxSizing: 'border-box', background: '#fff', color: '#242424', fontFamily: 'inherit', fontSize: 14,
    };
    const buttonBase: React.CSSProperties = {
        width: '100%', marginTop: '1em', padding: '5px 12px', borderRadius: 4, fontWeight: 600,
        fontFamily: 'inherit', fontSize: 14, cursor: 'default',
    };
    const primaryBase: React.CSSProperties = { ...buttonBase, border: 'none', background: '#0f6cbd', color: '#fff' };
    const subtleBase: React.CSSProperties = { ...buttonBase, border: 'none', background: 'transparent', color: '#242424' };

    const input = (placeholder: string) => (
        <div {...editable('form', 'input', 'fields')} style={{ position: 'relative' }}>
            {pencil('form', 'input', 'fields')}
            <input style={{ ...fieldBase, ...s('input') }} placeholder={placeholder} readOnly />
        </div>
    );
    const primaryButton = (label: string) => (
        <button {...editable('form', 'primaryButton', 'primary button')} style={{ ...primaryBase, position: 'relative', ...s('primaryButton') }}>
            {pencil('form', 'primaryButton', 'primary button')}{label}
        </button>
    );
    const secondaryButton = (label: string) => (
        <button {...editable('form', 'secondaryButton', 'secondary buttons')} style={{ ...subtleBase, position: 'relative', ...s('secondaryButton') }}>
            {pencil('form', 'secondaryButton', 'secondary buttons')}{label}
        </button>
    );
    // A labeled field, as the sign-up form renders them (Fluent <Field> puts its label above).
    const labeledField = (label: string, placeholder: string, required = false) => (
        <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                {label}{required && <span style={{ color: '#b10e1c' }}> *</span>}
            </label>
            {input(placeholder)}
        </div>
    );

    const sampleMessage = (
        <div
            {...editable('form', 'message', 'message bars')}
            style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1em', padding: '6px 10px',
                border: '1px solid #d1d1d1', borderRadius: 4, background: '#f5f5f5', fontSize: 12,
                position: 'relative', ...s('message'),
            }}
        >
            {pencil('form', 'message', 'message bars')}
            Your session expired. Please sign in again.
        </div>
    );

    const settings = props.storefrontSettings;
    const heading = config.headingText !== undefined ? config.headingText : settings.name;
    // The intro is copy, and the runtime only shows it on the sign-in page (it would read wrong on
    // sign-up) — the preview matches, or a merchant would style a line that never appears there.
    const showIntro = preview !== 'signup' && !!config.introText;

    return (
        <div>
            {props.editMode && (
                <div style={{ marginBottom: '1em' }}>
                    <RawJsonEditor
                        label='Sign In Page'
                        liveObject={config}
                        onApply={(json) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, loginPageConfig: json })}
                        // The four content keys are strings, not style sections — declared so the
                        // linter type-checks them instead of warning that they aren't style parts.
                        lint={(raw) => lintStyleSections(raw, LOGIN_PAGE_STYLE_PARTS, [], LOGIN_PAGE_CONTENT_KEYS)}
                        keysHint={`Sections: ${LOGIN_PAGE_STYLE_PARTS.join(', ')}. Text keys: ${LOGIN_PAGE_CONTENT_KEYS.join(', ')}. Any CSS property (camelCase, e.g. boxShadow) works. Numbers become px (use a string for other units, e.g. "600" for font-weight) and fontSize is a percent. url(), @import, javascript: and expression() are blocked. Changes apply while the JSON is valid.`}
                    >
                        <CommandBar
                            items={styleBarItems}
                            farItems={previewBarItems}
                            styles={{ root: { padding: 0, border: '1px solid #edebe9', borderRadius: 4, background: '#faf9f8' } }}
                        />
                    </RawJsonEditor>

                    {/* Page: the backdrop and the card. The background image lives here rather than in
                        a separate assets screen because it IS the backdrop's most consequential
                        setting, and the preview repaints as soon as it is picked. */}
                    <Panel isOpen={openPanel?.key === 'page'} onDismiss={closePanel} headerText='Page' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                        <ImageEditor
                            label='Background image'
                            value={config.backdropImageUrl}
                            onChange={(v) => write({ backdropImageUrl: v || undefined })}
                            onClear={() => write({ backdropImageUrl: undefined })}
                            height={90}
                            width={160}
                            imageId={settings.id}
                            imageType='Storefront'
                        />
                        <p style={{ margin: '0 0 1em', fontSize: 12, color: '#605e5c' }}>
                            Fills the page behind the card, sized to cover. A background color set on
                            Background below is layered over it, so a translucent color tints the image
                            rather than hiding it.
                        </p>
                        <StylePartAccordions key={openPanel?.part} initialOpenKey={openPanel?.part} parts={accordions('page')} />
                    </Panel>

                    {/* Branding: the logo and the two lines of text above the form. */}
                    <Panel isOpen={openPanel?.key === 'branding'} onDismiss={closePanel} headerText='Branding' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                        <ImageEditor
                            label='Logo'
                            value={config.logoUrl}
                            onChange={(v) => write({ logoUrl: v || undefined })}
                            onClear={() => write({ logoUrl: undefined })}
                            height={60}
                            width={160}
                            imageId={settings.id}
                            imageType='Storefront'
                        />
                        {/* Copied rather than referenced: the two are the same image for most stores,
                            but a login card often wants the full wordmark where the header carries a
                            compact mark, so they have to stay independently changeable. */}
                        {!!settings.headerLogo && settings.headerLogo !== config.logoUrl && (
                            <DefaultButton
                                text='Use the header logo'
                                iconProps={{ iconName: 'Copy' }}
                                onClick={() => write({ logoUrl: settings.headerLogo })}
                                styles={{ root: { marginBottom: 8 } }}
                            />
                        )}
                        <TextField
                            label='Heading'
                            placeholder={settings.name || 'Your storefront name'}
                            description='Replaces the store name. Type a space to hide it, which is what you want when the logo already spells it out.'
                            value={config.headingText ?? ''}
                            onChange={(e, v) => write({ headingText: v === '' ? undefined : v })}
                        />
                        <TextField
                            label='Intro text'
                            placeholder='Sign in with your work email'
                            description='Shown under the heading on the sign-in page only.'
                            value={config.introText ?? ''}
                            onChange={(e, v) => write({ introText: v || undefined })}
                            styles={{ root: { marginTop: 8, marginBottom: '1em' } }}
                        />
                        <StylePartAccordions key={openPanel?.part} initialOpenKey={openPanel?.part} parts={accordions('branding')} />
                    </Panel>

                    {/* Form: everything inside the card below the branding. */}
                    <Panel isOpen={openPanel?.key === 'form'} onDismiss={closePanel} headerText='Form' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                        <p style={{ margin: '0 0 0.5em', fontSize: 12, color: '#605e5c' }}>
                            The fields and buttons shoppers use to sign in or sign up, and the bars that
                            carry notices and errors.
                        </p>
                        <StylePartAccordions key={openPanel?.part} initialOpenKey={openPanel?.part} parts={accordions('form')} />
                    </Panel>
                </div>
            )}

            {/* ---------- The page itself (LoginPageShell + the hosted form) ----------
                Deliberately no header or footer: the runtime's auth pages have none. The backdrop is
                height-capped rather than 100vh so the preview sits in the editor's flow. */}
            <PreviewCanvas>
                <div
                    {...editable('page', 'page', 'page background')}
                    style={{
                        display: 'flex', minHeight: 560, padding: '2em 1em', boxSizing: 'border-box',
                        backgroundColor, color: textColor, fontFamily: defaultFont, position: 'relative',
                        ...(config.backdropImageUrl ? {
                            backgroundImage: `url(${config.backdropImageUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                        } : {}),
                        ...s('page'),
                    }}
                >
                    {pencil('page', 'page', 'page background')}
                    {/* .login-box / .signup-box: white, auto-margined, 20px padding, 5px radius, soft shadow */}
                    <div
                        {...editable('page', 'box', 'card')}
                        style={{
                            backgroundColor: '#fff', color: '#242424', margin: 'auto', padding: 20, borderRadius: 5,
                            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)', width: preview === 'signup' ? 420 : 320,
                            maxWidth: '100%', position: 'relative', ...s('box'),
                        }}
                    >
                        {pencil('page', 'box', 'card')}

                        {config.logoUrl ? (
                            <img
                                {...editable('branding', 'logo', 'logo')}
                                src={config.logoUrl}
                                alt=''
                                style={{ display: 'block', margin: '0 auto 1em', maxWidth: '100%', maxHeight: 80, ...s('logo') }}
                            />
                        ) : props.editMode && (
                            /* Edit mode only, and only while there is no logo: without it the card
                               shows nothing at all where a logo would go, and the only hint that a
                               login page CAN carry one is buried inside the Branding panel. Same
                               idiom as the header editor's logo placeholder. */
                            <div
                                {...editable('branding', 'logo', 'logo')}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                    margin: '0 auto 1em', height: 56, border: '1px dashed #c8c6c4', borderRadius: 4,
                                    color: '#605e5c', fontSize: 12, cursor: 'pointer',
                                }}
                            >
                                <Icon iconName='Photo2' />
                                Add a logo
                            </div>
                        )}
                        {/* An empty heading is a deliberate "no heading" — a wordmark logo already says it. */}
                        {!!heading && (
                            <h2 {...editable('branding', 'heading', 'heading')} style={{ position: 'relative', marginTop: 0, ...s('heading') }}>
                                {pencil('branding', 'heading', 'heading')}{heading}
                            </h2>
                        )}
                        {showIntro && (
                            <p {...editable('branding', 'intro', 'intro text')} style={{ position: 'relative', marginTop: 0, fontSize: 13, ...s('intro') }}>
                                {pencil('branding', 'intro', 'intro text')}{config.introText}
                            </p>
                        )}

                        {/* The sample notice bar, drawn where each page puts its own: above the form on
                            sign-in, under the "Sign Up" heading on sign-up. Always shown, because the
                            real one only appears on an expired session or a failed sign-in — states a
                            merchant can't summon on demand and would otherwise style blind. */}
                        {preview !== 'signup' && sampleMessage}

                        {preview === 'signin' && (
                            <React.Fragment>
                                {input('Email Address')}
                                {primaryButton('Login')}
                                {settings.allowSignup && secondaryButton('Sign Up')}
                                {settings.allowAnonymousAccess && secondaryButton('Browse as Guest')}
                            </React.Fragment>
                        )}

                        {preview === 'code' && (
                            <React.Fragment>
                                <p style={{ marginTop: 0, fontSize: 13 }}>Enter the code we emailed you.</p>
                                {input('000000')}
                                {primaryButton('Sign in')}
                                {secondaryButton('Use a different email')}
                            </React.Fragment>
                        )}

                        {preview === 'signup' && (
                            <React.Fragment>
                                <h3 style={{ marginTop: 0 }}>Sign Up</h3>
                                {sampleMessage}
                                {labeledField('Email Address', 'Email Address', true)}
                                {labeledField('First Name', 'First Name', true)}
                                {labeledField('Last Name', 'Last Name', true)}
                                {labeledField('Company Name', 'Company Name')}
                                {labeledField('Phone Number', 'Phone Number')}
                                {!!settings.signupTermsAndConditions && (
                                    // Rendered as markdown, like the shopper page does — raw asterisks
                                    // in the preview would misrepresent how much room the terms take.
                                    // The terms are authored on the Authentication settings page and
                                    // take no styling from this config, so they carry no pencil.
                                    <div style={{ margin: '1em 0', padding: 8, border: '1px solid #edebe9', borderRadius: 4, maxHeight: 120, overflowY: 'auto', fontSize: 12 }}>
                                        <MarkdownView markdown={settings.signupTermsAndConditions} options={{ tables: true, emoji: true }} />
                                    </div>
                                )}
                                {/* The bot check is Cloudflare's own iframe — it takes no styling from
                                    this config, so it is drawn as the fixed-size block it occupies. */}
                                <div style={{ margin: '1em 0', height: 65, border: '1px dashed #c8c6c4', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#605e5c' }}>
                                    Bot check (not styleable)
                                </div>
                                {primaryButton('Submit')}
                            </React.Fragment>
                        )}
                    </div>
                </div>
            </PreviewCanvas>

            {props.editMode && (
                <p style={{ marginTop: '0.75em', fontSize: 12, color: '#605e5c' }}>
                    These settings also style the two pages an emailed sign-in link lands on, so the
                    whole flow matches. Shoppers who reach the shared sign-in page without a storefront
                    in the URL see the default styling — there are no storefront settings to read there.
                </p>
            )}
        </div>
    );
};

export default StorefrontLoginPageEditor;
